import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { BaseUITools } from '../baseUITools';
import { getI18nText } from '@/utils/commonFunc';

import '@/uiControls/styles/components.scss';

export interface ColorSelectorOptions extends ElementOptions {
    /** 标签文本 */
    label?: string;
    /** 是否显示标签 */
    showLabel?: boolean;
    /** 是否显示当前值 */
    showValue?: boolean;
    /** 初始颜色值 */
    color?: string;
    /** 国际化key */
    i18nKey?: string;
    containerClass?: string;
    labelClass?: string;
    valueClass?: string;
}

export class ColorSelectorAntUI extends BaseUITools {
    private currentColor: string;
    private colorPickerElement!: HTMLElement;
    private colorPreviewElement!: HTMLElement;
    private colorValueElement?: HTMLInputElement;
    private colorPickerPanel!: HTMLElement;
    private options: ColorSelectorOptions;
    private onChangeCallbacks: ((color: string) => void)[] = [];
    private isUpdating: boolean = false; // 防止循环调用的标志
    private isPanelOpen: boolean = false;
    private hsv: { h: number; s: number; v: number } = { h: 0, s: 100, v: 100 };

    constructor(options: ColorSelectorOptions = {}) {
        super('color-selector-container');
        this.options = {
            label: '颜色选择器',
            showLabel: true,
            showValue: true,
            containerClass: 'color-selector-container',
            labelClass: 'color-selector-label',
            valueClass: 'color-selector-value',
            ...options
        };
        this.currentColor = options.color || '#ffffff';
        this.hsv = this.hexToHsv(this.currentColor);
        this.container = this.createElement();
        this.initializeColorPicker();
        this.setupEventListeners();
        // 初始化显示值
        this.updateColor(this.currentColor, false);
    }

    private createElement(): HTMLElement {
        const container = this.ui.createElement({
            tag: 'div',
            className: ['vision-color-picker-wrapper', 'ant-color-picker-wrapper', ...(this.options.containerClass ? [this.options.containerClass] : [])]
        });
        // 设置容器为相对定位，以便面板可以相对于它定位
        container.style.position = 'relative';

        const pickerElement = this.ui.createElement({
            tag: 'div',
            className: ['vision-color-picker-content', 'ant-color-picker']
        });
        
        if (this.options.showLabel) {
            const labelElement = this.ui.createElement({
                tag: 'div',
                className: [...(this.options.labelClass ? [this.options.labelClass] : [])],
                textContent: this.options.label || '',
                attributes: {
                    ...(this.options.i18nKey ? { 'data-i18n': this.options.i18nKey } : {})
                }
            });
            container.appendChild(labelElement);
        }

        // 创建颜色选择器触发器
        const triggerWrapper = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-trigger']
        });

        const previewElement = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-color-block']
        });
        this.colorPreviewElement = previewElement;
        triggerWrapper.appendChild(previewElement);

        this.colorPickerElement = triggerWrapper;
        pickerElement.appendChild(triggerWrapper);

        // 创建颜色值输入框
        if (this.options.showValue) {
            const valueElement = this.ui.createElement({
                tag: 'input',
                className: ['vision-color-value', 'input-control', 'ant-input', ...(this.options.valueClass ? [this.options.valueClass] : [])],
                attributes: {
                    type: 'text'
                }
            });
            this.colorValueElement = valueElement as HTMLInputElement;
            pickerElement.appendChild(valueElement);
        }

        container.appendChild(pickerElement);

        return container;
    }

    private initializeColorPicker(): void {
        // 创建颜色选择面板
        this.createColorPickerPanel();
        
        // 点击触发器打开/关闭面板
        this.colorPickerElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.togglePanel();
        });

        // 点击外部关闭面板
        const handleDocumentClick = (e: MouseEvent) => {
            if (this.isPanelOpen && 
                !this.container.contains(e.target as Node) && 
                !this.colorPickerPanel.contains(e.target as Node)) {
                this.closePanel();
            }
        };
        document.addEventListener('click', handleDocumentClick);
        
        // 保存事件处理器以便销毁时移除
        (this as any)._documentClickHandler = handleDocumentClick;
    }

    private createColorPickerPanel(): void {
        const panel = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-panel', 'ant-popover-content']
        });

        // 创建颜色面板
        const colorPanel = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-panel-inner']
        });

        // 创建饱和度/亮度选择区域
        const saturationPanel = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-saturation']
        });
        saturationPanel.style.position = 'relative';
        saturationPanel.style.width = '280px';
        saturationPanel.style.height = '180px';
        saturationPanel.style.cursor = 'crosshair';
        saturationPanel.style.background = `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), hsl(${this.hsv.h}, 100%, 50%)`;
        
        // 创建选择点
        const handle = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-handle']
        });
        handle.style.position = 'absolute';
        handle.style.width = '12px';
        handle.style.height = '12px';
        handle.style.border = '2px solid #fff';
        handle.style.borderRadius = '50%';
        handle.style.boxShadow = '0 0 2px rgba(0,0,0,0.3)';
        handle.style.transform = 'translate(-50%, -50%)';
        handle.style.pointerEvents = 'none';
        saturationPanel.appendChild(handle);

        // 创建色相滑块
        const hueSlider = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-hue']
        });
        hueSlider.style.position = 'relative';
        hueSlider.style.width = '280px';
        hueSlider.style.height = '12px';
        hueSlider.style.marginTop = '12px';
        hueSlider.style.cursor = 'pointer';
        hueSlider.style.background = 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)';
        hueSlider.style.borderRadius = '2px';

        const hueHandle = this.ui.createElement({
            tag: 'div',
            className: ['ant-color-picker-handle']
        });
        hueHandle.style.position = 'absolute';
        hueHandle.style.width = '12px';
        hueHandle.style.height = '12px';
        hueHandle.style.border = '2px solid #fff';
        hueHandle.style.borderRadius = '2px';
        hueHandle.style.boxShadow = '0 0 2px rgba(0,0,0,0.3)';
        hueHandle.style.transform = 'translate(-50%, -50%)';
        hueHandle.style.pointerEvents = 'none';
        hueHandle.style.top = '50%';
        hueSlider.appendChild(hueHandle);

        // 添加交互事件
        this.setupSaturationPanel(saturationPanel, handle);
        this.setupHueSlider(hueSlider, hueHandle);

        colorPanel.appendChild(saturationPanel);
        colorPanel.appendChild(hueSlider);
        panel.appendChild(colorPanel);

        this.colorPickerPanel = panel;
        // 将面板添加到 body，而不是 container，以便使用 fixed 定位
        document.body.appendChild(panel);
        panel.style.display = 'none';
        panel.style.position = 'fixed';
        panel.style.zIndex = '1050';
        panel.style.background = '#fff';
        panel.style.borderRadius = '6px';
        panel.style.boxShadow = '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)';
        panel.style.padding = '12px';
        
        // 阻止面板内部点击事件冒泡
        panel.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    private setupSaturationPanel(panel: HTMLElement, handle: HTMLElement): void {
        let isDragging = false;

        const updateHandle = (x: number, y: number) => {
            const rect = panel.getBoundingClientRect();
            const s = Math.max(0, Math.min(100, (x - rect.left) / rect.width * 100));
            const v = Math.max(0, Math.min(100, 100 - (y - rect.top) / rect.height * 100));
            
            this.hsv.s = s;
            this.hsv.v = v;
            
            handle.style.left = `${s}%`;
            handle.style.top = `${100 - v}%`;
            
            panel.style.background = `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), hsl(${this.hsv.h}, 100%, 50%)`;
            
            const hex = this.hsvToHex(this.hsv);
            this.updateColor(hex, true);
        };

        panel.addEventListener('mousedown', (e) => {
            isDragging = true;
            updateHandle(e.clientX, e.clientY);
        });

        document.addEventListener('mousemove', (e) => {
            if (isDragging) {
                updateHandle(e.clientX, e.clientY);
            }
        });

        document.addEventListener('mouseup', () => {
            isDragging = false;
        });
    }

    private setupHueSlider(slider: HTMLElement, handle: HTMLElement): void {
        let isDragging = false;

        const updateHandle = (x: number) => {
            const rect = slider.getBoundingClientRect();
            const h = Math.max(0, Math.min(360, (x - rect.left) / rect.width * 360));
            
            this.hsv.h = h;
            
            handle.style.left = `${(h / 360) * 100}%`;
            
            // 更新饱和度面板背景
            const saturationPanel = slider.previousElementSibling as HTMLElement;
            if (saturationPanel) {
                saturationPanel.style.background = `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), hsl(${this.hsv.h}, 100%, 50%)`;
            }
            
            const hex = this.hsvToHex(this.hsv);
            this.updateColor(hex, true);
        };

        slider.addEventListener('mousedown', (e) => {
            isDragging = true;
            updateHandle(e.clientX);
        });

        document.addEventListener('mousemove', (e) => {
            if (isDragging) {
                updateHandle(e.clientX);
            }
        });

        document.addEventListener('mouseup', () => {
            isDragging = false;
        });
    }

    private togglePanel(): void {
        if (this.isPanelOpen) {
            this.closePanel();
        } else {
            this.openPanel();
        }
    }

    private openPanel(): void {
        if (this.colorPickerPanel) {
            this.colorPickerPanel.style.display = 'block';
            this.isPanelOpen = true;
            // 使用 requestAnimationFrame 确保 DOM 更新后再计算位置
            requestAnimationFrame(() => {
                this.updatePanelPosition();
            });
        }
    }

    private closePanel(): void {
        if (this.colorPickerPanel) {
            this.colorPickerPanel.style.display = 'none';
            this.isPanelOpen = false;
        }
    }

    private updatePanelPosition(): void {
        if (!this.colorPickerPanel || !this.colorPickerElement) return;
        
        const triggerRect = this.colorPickerElement.getBoundingClientRect();
        
        // 使用 fixed 定位，相对于视口
        let top = triggerRect.bottom + 4;
        let left = triggerRect.left;
        
        // 获取面板尺寸（如果面板已显示）
        const panelRect = this.colorPickerPanel.getBoundingClientRect();
        const panelWidth = panelRect.width || 304; // 默认宽度：280 + 12*2 padding
        const panelHeight = panelRect.height || 204; // 默认高度：180 + 12 + 12 padding
        
        // 检查右侧是否超出视口
        if (left + panelWidth > window.innerWidth) {
            left = window.innerWidth - panelWidth - 10;
        }
        
        // 检查底部是否超出视口，如果超出则显示在上方
        if (top + panelHeight > window.innerHeight) {
            top = triggerRect.top - panelHeight - 4;
        }
        
        // 确保不会超出左侧
        if (left < 10) {
            left = 10;
        }
        
        // 确保不会超出顶部
        if (top < 10) {
            top = 10;
        }
        
        this.colorPickerPanel.style.top = `${top}px`;
        this.colorPickerPanel.style.left = `${left}px`;
    }

    private setupEventListeners(): void {
        if (!this.colorValueElement) return;

        // 监听颜色值输入框的输入事件，过滤和格式化输入
        this.colorValueElement.addEventListener('input', (event) => {
            const input = event.target as HTMLInputElement;
            let value = input.value;
            const cursorPosition = input.selectionStart || 0;

            // 保存 0x 前缀
            const hasPrefix = value.toLowerCase().startsWith('0x');
            let hexValue = hasPrefix ? value.slice(2) : value;

            // 只保留数字 0-9 和大写字母 A-F，小写自动转大写
            hexValue = hexValue.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
            
            // 限制长度为6个字符（标准颜色值 RRGGBB）
            hexValue = hexValue.slice(0, 6);

            // 如果没有 0x 前缀，自动添加
            const newValue = '0x' + hexValue;

            // 更新输入框的值
            input.value = newValue;

            // 计算新的光标位置：统计光标之前有多少个有效的十六进制字符
            const textBeforeCursor = hasPrefix ? value.slice(2, cursorPosition) : value.slice(0, cursorPosition);
            const validCharsBeforeCursor = textBeforeCursor.replace(/[^0-9A-Fa-f]/gi, '').length;
            // 新光标位置 = 0x (2个字符) + 有效字符数量
            const newCursorPosition = Math.min(2 + validCharsBeforeCursor, newValue.length);
            input.setSelectionRange(newCursorPosition, newCursorPosition);
        });

        // 监听颜色值输入框的回车事件
        this.colorValueElement.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                const inputValue = (event.target as HTMLInputElement).value.trim();
                // 将 0x 格式转换为 # 格式进行验证和更新
                const colorValue = inputValue.replace(/^0x/i, '#');
                // 验证是否为有效的十六进制颜色值 (#RRGGBB 或 #RRGGBBAA)
                if (this.isValidHexColor(colorValue)) {
                    this.updateColor(colorValue, true);
                } else {
                    // 如果输入无效，恢复为当前颜色值
                    // this.colorValueElement.value = this.currentColor;
                }
            }
        });

        // 监听失去焦点事件，如果输入无效，恢复为当前颜色值
        this.colorValueElement.addEventListener('blur', () => {
            if (!this.colorValueElement) return;
            const inputValue = this.colorValueElement.value.trim();
            // 将 0x 格式转换为 # 格式进行验证
            const colorValue = inputValue.replace(/^0x/i, '#');
            if (!this.isValidHexColor(colorValue)) {
                // 恢复为当前颜色值（转换为 0x 格式显示）
                const hexValue = this.currentColor.replace(/^#/, '');
                this.colorValueElement.value = '0x' + hexValue.toUpperCase();
            }
        });
    }

    /**
     * 验证是否为有效的十六进制颜色值
     * @param color 颜色值字符串
     * @returns 是否为有效的十六进制颜色值
     */
    private isValidHexColor(color: string): boolean {
        // 匹配 #RRGGBB 或 #RRGGBBAA 格式
        const hexColorPattern = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
        return hexColorPattern.test(color);
    }

    private updateColor(color: string, triggerCallbacks: boolean = false): void {
        if (this.isUpdating) return;
        
        // 规范化颜色值（确保是 #RRGGBB 格式）
        const normalizedColor = color.startsWith('#') ? color : '#' + color.replace(/^0x/i, '');
        
        // 如果颜色没有改变，直接返回
        if (normalizedColor === this.currentColor && !triggerCallbacks) {
            return;
        }
        
        this.isUpdating = true;
        this.currentColor = normalizedColor;
        this.hsv = this.hexToHsv(normalizedColor);
        
        // 更新预览颜色
        this.colorPreviewElement.style.backgroundColor = normalizedColor;
        
        // 更新输入框值
        if (this.colorValueElement) {
            const hexValue = normalizedColor.replace(/^#/, '');
            this.colorValueElement.value = '0x' + hexValue.toUpperCase();
        }

        // 更新颜色选择面板
        this.updateColorPanel();

        if (triggerCallbacks) {
            this.onChangeCallbacks.forEach(callback => {
                try {
                    callback(normalizedColor);
                } catch (error) {
                    console.error('Error in color change callback:', error);
                }
            });
        }
        
        this.isUpdating = false;
    }

    private updateColorPanel(): void {
        if (!this.colorPickerPanel) return;
        
        const saturationPanel = this.colorPickerPanel.querySelector('.ant-color-picker-saturation') as HTMLElement;
        const saturationHandle = saturationPanel?.querySelector('.ant-color-picker-handle') as HTMLElement;
        const hueSlider = this.colorPickerPanel.querySelector('.ant-color-picker-hue') as HTMLElement;
        const hueHandle = hueSlider?.querySelector('.ant-color-picker-handle') as HTMLElement;
        
        if (saturationPanel && saturationHandle) {
            saturationPanel.style.background = `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), hsl(${this.hsv.h}, 100%, 50%)`;
            saturationHandle.style.left = `${this.hsv.s}%`;
            saturationHandle.style.top = `${100 - this.hsv.v}%`;
        }
        
        if (hueHandle) {
            hueHandle.style.left = `${(this.hsv.h / 360) * 100}%`;
        }
    }

    public setColor(color: string, triggerCallbacks: boolean = false): void {
        this.updateColor(color, triggerCallbacks);
    }

    public getColor(): string {
        return this.currentColor;
    }

    /**
     * 设置颜色选择器的禁用状态
     * @param disabled 如果为 true，则禁用颜色选择器；如果为 false，则启用
     */
    public setDisabled(disabled: boolean): void {
        if (this.colorPickerElement) {
            if (disabled) {
                this.colorPickerElement.style.pointerEvents = 'none';
                this.colorPickerElement.style.opacity = '0.5';
                this.closePanel();
            } else {
                this.colorPickerElement.style.pointerEvents = 'auto';
                this.colorPickerElement.style.opacity = '1';
            }
        }
    }

    /**
     * 注册颜色改变时的回调函数
     * @param callback 颜色改变时调用的回调函数，参数为新颜色值
     * @returns 返回一个函数，调用该函数可以移除这个回调
     */
    public onChange(callback: (color: string) => void): () => void {
        this.onChangeCallbacks.push(callback);
        // 返回移除回调的函数
        return () => {
            const index = this.onChangeCallbacks.indexOf(callback);
            if (index > -1) {
                this.onChangeCallbacks.splice(index, 1);
            }
        };
    }

    /**
     * 移除所有颜色改变的回调函数
     */
    public removeAllCallbacks(): void {
        this.onChangeCallbacks = [];
    }

    /**
     * 获取容器元素
     */
    public getElement(): HTMLElement {
        return this.container;
    }

    /**
     * 销毁颜色选择器实例
     */
    public destroy(): void {
        // 移除文档点击事件监听
        if ((this as any)._documentClickHandler) {
            document.removeEventListener('click', (this as any)._documentClickHandler);
        }
        
        // 移除面板
        if (this.colorPickerPanel) {
            this.colorPickerPanel.remove();
        }
    }

    // 颜色转换工具方法
    private hexToHsv(hex: string): { h: number; s: number; v: number } {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const diff = max - min;

        let h = 0;
        if (diff !== 0) {
            if (max === r) {
                h = ((g - b) / diff) % 6;
            } else if (max === g) {
                h = (b - r) / diff + 2;
            } else {
                h = (r - g) / diff + 4;
            }
        }
        h = Math.round(h * 60);
        if (h < 0) h += 360;

        const s = max === 0 ? 0 : Math.round((diff / max) * 100);
        const v = Math.round(max * 100);

        return { h, s, v };
    }

    private hsvToHex(hsv: { h: number; s: number; v: number }): string {
        const h = hsv.h / 360;
        const s = hsv.s / 100;
        const v = hsv.v / 100;

        let r = 0, g = 0, b = 0;

        const i = Math.floor(h * 6);
        const f = h * 6 - i;
        const p = v * (1 - s);
        const q = v * (1 - f * s);
        const t = v * (1 - (1 - f) * s);

        switch (i % 6) {
            case 0: r = v; g = t; b = p; break;
            case 1: r = q; g = v; b = p; break;
            case 2: r = p; g = v; b = t; break;
            case 3: r = p; g = q; b = v; break;
            case 4: r = t; g = p; b = v; break;
            case 5: r = v; g = p; b = q; break;
        }

        const toHex = (n: number) => {
            const hex = Math.round(n * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
}